// src/services/announcementService.js
//
// I/O for home page announcements (UI revamp). Rules: announcements.js and
// firestore.rules. Posting and removing each write the post AND its history
// entry in one batch (the rules refuse one without the other). Nothing is
// ever deleted from here.

import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import { db } from "../firebase";
import { getAnnouncementsPath, getAnnouncementSeenPath } from "./firestorePaths";

const shape = (d) => ({ id: d.id, ...d.data() });
const col = () => collection(db, getAnnouncementsPath());

/** Everything addressed to someone at this company + site (the rules allow exactly these). */
export const loadAnnouncementsFor = async ({ tenantId, siteId }) => {
  const reads = [getDocs(query(col(), where("scope", "==", "all")))];
  if (tenantId) reads.push(getDocs(query(col(), where("scope", "==", "tenant"), where("tenantId", "==", tenantId))));
  if (tenantId && siteId) {
    reads.push(getDocs(query(col(), where("scope", "==", "site"), where("tenantId", "==", tenantId), where("siteId", "==", siteId))));
  }
  const snaps = await Promise.all(reads);
  return snaps.flatMap((s) => s.docs.map(shape));
};

/**
 * Posts an admin might manage: owners read the lot; company admins their
 * company's posts and every site's; site admins their own sites'. The page
 * then keeps only what canManage() allows.
 */
export const loadManageable = async ({ access, tenantId, adminSiteIds = [] }) => {
  if (access?.isOwner) return (await getDocs(col())).docs.map(shape);
  const reads = [];
  if (tenantId && access?.isTenantAdmin?.(tenantId)) {
    reads.push(getDocs(query(col(), where("scope", "==", "tenant"), where("tenantId", "==", tenantId))));
    reads.push(getDocs(query(col(), where("scope", "==", "site"), where("tenantId", "==", tenantId))));
  } else {
    adminSiteIds.forEach((siteId) =>
      reads.push(getDocs(query(col(), where("scope", "==", "site"), where("tenantId", "==", tenantId), where("siteId", "==", siteId))))
    );
  }
  const snaps = await Promise.all(reads);
  return snaps.flatMap((s) => s.docs.map(shape));
};

// The signed-in person's email exactly as on their login token: the rules
// compare against request.auth.token.email, so it must not be reformatted.
const emailOf = (user) => String((user && user.email) || "");

/**
 * Post (data + targets from validatePost): one post per audience, each with its
 * "posted" history entry, all in one batch (all or nothing). Several audiences
 * share a groupId so they're shown and removed as one.
 */
export const postAnnouncement = async ({ data, targets, user }) => {
  const b = writeBatch(db);
  const groupId = targets.length > 1 ? doc(col()).id : null;
  const ids = targets.map((t) => {
    const ref = doc(col());
    b.set(ref, {
      ...data,
      scope: t.scope,
      tenantId: t.tenantId,
      siteId: t.siteId,
      ...(groupId ? { groupId } : {}),
      postedByEmail: emailOf(user),
      postedByUid: user.uid,
      postedAt: serverTimestamp(),
    });
    b.set(doc(db, getAnnouncementsPath(), ref.id, "audit", "posted"), {
      action: "posted",
      byUid: user.uid,
      byEmail: emailOf(user),
      at: serverTimestamp(),
      title: data.title,
    });
    return ref.id;
  });
  await b.commit();
  return ids;
};

/**
 * Remove (one post, or a group posted together): stamp removedAt/By once on
 * each (the records are kept) + each one's "removed" history entry.
 */
export const removeAnnouncement = async ({ posts, user }) => {
  const b = writeBatch(db);
  (posts || [])
    .filter((p) => !p.removedAt)
    .forEach((p) => {
      b.update(doc(db, getAnnouncementsPath(), p.id), {
        removedAt: serverTimestamp(),
        removedByUid: user.uid,
        removedByEmail: emailOf(user),
      });
      b.set(doc(db, getAnnouncementsPath(), p.id, "audit", "removed"), {
        action: "removed",
        byUid: user.uid,
        byEmail: emailOf(user),
        at: serverTimestamp(),
        title: p.title,
      });
    });
  await b.commit();
};

/** When this person was last shown the announcements (ms), or null if never. */
export const loadSeenAt = async (uid) => {
  const snap = await getDoc(doc(db, getAnnouncementSeenPath(), uid));
  const v = snap.exists() ? snap.data().seenAt : null;
  return v && typeof v.toMillis === "function" ? v.toMillis() : null;
};

/** Remember "shown now" (the server's time). */
export const markSeen = async (uid) => {
  await setDoc(doc(db, getAnnouncementSeenPath(), uid), { seenAt: serverTimestamp() });
};
